import { createContext, FC, useContext, useState } from "react";
import * as _ from "lodash";
import { useSelector } from "react-redux";

import { DataDescriptorList, Enterprise as App, EnterpriseAppState, EventScope, Subject } from "@ctra/api";
import { useEvent } from "@events";
import { CreateAskIdaOrigin, useCreateAskIdaTrackerDialog } from "../CreateTrackerDialogContext";

export enum Step {
  eventSummary = "eventSummary",
  prompt = "prompt",
  configure = "configure",
  trackingSummary = "trackingSummary"
}

interface BaseContext {
  steps: {
    list: Array<Step>;
    step: number;
  };
  api: {
    findIndex: (step: Step) => number;
    goTo: (step: Step) => void;
  };
  meta: {
    kpis: DataDescriptorList;
  };
}

// istanbul ignore next - not testing default context
const DefaultContext = createContext<BaseContext>({
  steps: {
    list: [],
    step: 0
  },
  api: {
    goTo: () => void 0,
    findIndex: () => -1
  },
  meta: {
    kpis: {}
  }
});

/**
 * Context provider to help creating an Ask Ida tracker
 * @param children
 */
const _CreateAskIdaTrackerContextProvider: FC = ({ children }) => {
  const {
    searchParams: { origin }
  } = useCreateAskIdaTrackerDialog();

  const [currentStep, goTo] = useState<Step>(
    origin === CreateAskIdaOrigin.timeline ? Step.configure : Step.prompt
  );

  const {
    event: {
      scope: { type: eventScope }
    }
  } = useEvent();

  const steps =
    currentStep === Step.prompt
      ? _.uniq([Step.eventSummary, Step.prompt, currentStep])
      : [Step.eventSummary, currentStep];

  const stepNumber = _.findIndex(steps, _.matches(currentStep));

  const kpiList = useSelector<EnterpriseAppState, DataDescriptorList>((state) => {
    const list = App.entities.getDataDictionary(state, {
      filter: {
        flags: {
          isAskIdaSupported: true
        }
      }
    });

    /**
     * @todo hook up "cow" when applicable
     * @type {DataDescriptorList}
     */
    const result: DataDescriptorList = _.pickBy(
      list,
      ({ dataProperties: { askIdaMappings } }) =>
        _.get(askIdaMappings, "subjectType") ===
        _.camelCase(eventScope === EventScope.farm ? Subject.farm : Subject.herdGroup)
    );

    return result;
  });

  return (
    <DefaultContext.Provider
      value={{
        steps: {
          list: steps,
          step: stepNumber
        },
        api: {
          findIndex: (step) => _.findIndex(steps, _.matches(step)),
          goTo
        },
        meta: {
          kpis: kpiList
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const CreateAskIdaTrackerContext = {
  Provider: _CreateAskIdaTrackerContextProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * @returns {void}
 */
export const useCreateAskIdaTracker = (): BaseContext => useContext<BaseContext>(DefaultContext);
